import Decimal from 'decimal.js';
import { kgPerBase, type PriceBasis } from './pricing';
import { PRICE_DP, QTY_DP } from './quote-calc';
import { isKgUnit, sameUnit, toBasePrice, type UomOption } from './uom';

/**
 * CR-009: bảng giá nhà cung cấp (NCC) dạng JSON cho AI/agent đọc, quy giá NCC về ĐVT chính, đề xuất giá bán và số lượng nhập.
 * Giá NCC luôn lưu CHƯA VAT (DEC-005); bảng ghi giá đã gồm VAT thì chia (1 + VAT) lúc nhập.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

/** Định danh cấu trúc JSON bảng giá NCC – đổi khi thêm/bớt trường không tương thích. */
export const SUPPLIER_PRICE_DOC_SCHEMA = 'mt.supplier-price-list/v1';

/** Mặc định lãi đề xuất giá bán từ giá NCC (anh chưa chốt, sửa được theo từng bảng – CR-009). */
export const DEFAULT_SUPPLIER_MARKUP_PERCENT = 10;

export interface SupplierPriceDocItem {
  line: number;
  /** Mã Asia đã gắn (chuẩn, DEC-007); null = chưa gắn mã */
  sku: string | null;
  supplierCode: string | null;
  name: string;
  spec: string | null;
  brand: string | null;
  /** ĐVT NCC ghi */
  unit: string | null;
  /** Giá in trong bảng NCC (chưa trừ VAT, đúng như tài liệu gốc) */
  rawPrice: number;
  /** Giá chưa VAT theo ĐVT NCC */
  price: number;
  priceBasis: PriceBasis;
  /** Giá chưa VAT trên 1 ĐVT chính của mã Asia (null = chưa quy đổi được) */
  basePrice: number | null;
  baseUnit: string | null;
  vatRate: number | null;
  minQty: number | null;
  note: string | null;
  /** auto = máy tự khớp, review = cần xem, confirmed = NV đã xác nhận, unmatched = chưa gắn */
  mapping: 'auto' | 'review' | 'confirmed' | 'unmatched';
  /** Truy vết về tài liệu gốc */
  source: { sheet: string | null; page: number | null; row: number | null };
}

/** Tài liệu JSON 1 bảng giá NCC (GET /supplier-price-lists/:id/json, công cụ AI). */
export interface SupplierPriceDoc {
  schema: typeof SUPPLIER_PRICE_DOC_SCHEMA;
  id: number;
  supplier: { id: number; code: string; name: string; asiaCode: string | null };
  title: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: 'active' | 'archived';
  currency: 'VND';
  pricesIncludeVat: false;
  markupPercent: number;
  /** Ưu tiên NCC (số nhỏ dùng trước; cùng ưu tiên thì rẻ hơn dùng trước) */
  priority: number;
  source: { kind: string; fileName: string | null; fileHash: string | null };
  items: SupplierPriceDocItem[];
}

export type OfferUnitResult =
  | { ok: true; basis: PriceBasis; /** giá theo basis: giá/kg hoặc giá 1 ĐVT chính */ price: number; basePrice: number | null }
  | { ok: false; reason: string };

/**
 * Quy giá NCC (chưa VAT, theo ĐVT NCC) về mã Asia:
 * - ĐVT trống hoặc trùng ĐVT chính → giá 1 ĐVT chính;
 * - trùng ĐVT phụ → × hệ số (1 ĐVT chính = factor × ĐVT phụ);
 * - ĐVT kg mà mã không bán theo kg chính → giá/kg (basis kg), giá ĐVT chính = giá/kg × kg/ĐVT chính (nếu có quy đổi);
 * - ĐVT khác → không quy được (NV sửa ĐVT hoặc gắn mã khác).
 */
export function offerUnitPrice(price: number | string, unit: string | null | undefined, options: UomOption[], weightKg?: number | string | null): OfferUnitResult {
  const p = new D(price).toDecimalPlaces(PRICE_DP);
  if (!p.gt(0)) return { ok: false, reason: 'Giá phải lớn hơn 0' };
  const base = options[0]!;
  if (!unit?.trim() || sameUnit(unit, base.uom)) return { ok: true, basis: 'base_uom', price: p.toNumber(), basePrice: p.toNumber() };
  const kg = kgPerBase(options, weightKg);
  if (isKgUnit(unit)) {
    return { ok: true, basis: 'kg', price: p.toNumber(), basePrice: kg ? p.mul(kg).toDecimalPlaces(PRICE_DP).toNumber() : null };
  }
  const aux = options.find((o) => !o.isBase && sameUnit(o.uom, unit));
  if (aux) {
    const bp = toBasePrice(p.toNumber(), aux.factor);
    return { ok: true, basis: 'base_uom', price: bp, basePrice: bp };
  }
  return { ok: false, reason: `ĐVT "${unit}" của NCC không có trong quy đổi của mã (chọn: ${options.map((o) => o.uom).join(', ')})` };
}

/** Giá chưa VAT từ giá đã gồm VAT: giá ÷ (1 + VAT%), 2 số lẻ. */
export function exVatPrice(price: number | string, vatRate: number | string): number {
  return new D(price).div(new D(100).add(vatRate)).mul(100).toDecimalPlaces(PRICE_DP).toNumber();
}

/** Giá bán đề xuất từ giá NCC: round(giá × (1 + lãi%)) VND. */
export function suggestSalePrice(cost: number | string, markupPercent: number | string): number {
  return new D(cost).toDecimalPlaces(PRICE_DP).mul(new D(100).add(new D(markupPercent).toDecimalPlaces(3))).div(100).toDecimalPlaces(0).toNumber();
}

/**
 * Số lượng nên nhập (ĐVT chính): thiếu = cần − tồn (không âm); có SL tối thiểu của NCC thì làm tròn LÊN theo bội số SL tối thiểu.
 * stock null (không đọc được tồn Asia) → coi như tồn 0.
 */
export function suggestPurchaseQty(need: number | string, stock: number | string | null | undefined, minQty?: number | string | null): number {
  const short = D.max(0, new D(need).minus(stock ?? 0)).toDecimalPlaces(QTY_DP);
  if (short.isZero()) return 0;
  if (minQty == null || !new D(minQty).gt(0)) return short.toNumber();
  const m = new D(minQty).toDecimalPlaces(QTY_DP);
  return short.div(m).ceil().mul(m).toDecimalPlaces(QTY_DP).toNumber();
}
